import castArray from 'lodash/castArray'
import first from 'lodash/first'
import trim from 'lodash/trim'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import React from 'react'

import { BackToTop } from '@Components/BackToTop'
import { FooterWithGroupVisibility } from '@Components/Footer'
import { HeaderWithGroupVisibility } from '@Components/Header'
import { Page, PageType } from '@Generated/graphql'
import { initializeApolloFromContext } from '@Globals/Apollo/Server'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { PageHOC } from '@Globals/Page/Client'
import { boostFromContext, handleRedirect, pageProps } from '@Globals/Page/Server'
import { PageProps } from '@Globals/Page/Types'
import { getStoreURLByContext } from '@Globals/Stores'
import { OneColumn } from '@Layouts/OneColumn'
import { getSinglePost } from '@Queries/cms/SinglePost.query'

interface SearchResultsProps extends PageProps {
  item: Page
  q: string
}

export const getServerSideProps: GetServerSideProps<SearchResultsProps> = async (context) => {
  const apolloClient = initializeApolloFromContext(context)
  const post = await getSinglePost(
    apolloClient,
    PageType.Search,
    '/search/results',
    undefined,
    boostFromContext(context),
    context?.req?.thSplitTests,
  )
  const { page } = post.data

  if (!page) {
    return {
      notFound: true,
    }
  }

  if (page?.type === 'redirect_permanent' || page?.type === 'redirect_temporary') {
    return handleRedirect(context, page.url, page.type)
  }

  const q = trim(first(castArray(context.query.q)))

  if (q === '') {
    return {
      redirect: {
        destination: getStoreURLByContext(context, ''),
        permanent: false,
      },
    }
  }

  const props = await pageProps(
    context,
    {
      type: 'searchresults',
      id: '',
    },
    page,
    ['cart', 'cms', 'common', 'footer', 'giftcard', 'header', 'products', 'shipping', 'sidebar', 'validation'],
    true,
  )

  const totalHits = Object.values(props?.swrFallback)?.[0]?.totalHits
  if (typeof totalHits === 'number' && totalHits === 0) {
    const noResultsPost = await getSinglePost(
      apolloClient,
      PageType.Search,
      '/search/no-results',
      undefined,
      boostFromContext(context),
      context?.req?.thSplitTests,
    )
    const { page: noResultsPage } = noResultsPost.data

    if (!noResultsPage) {
      return {
        notFound: true,
      }
    }

    return {
      props: {
        ...await pageProps(
          context,
          {
            type: 'searchresults',
            id: '',
          },
          noResultsPage,
          ['cart', 'cms', 'common', 'footer', 'giftcard', 'header', 'products', 'shipping', 'sidebar', 'validation'],
          true,
        ),
        item: noResultsPage as Page,
        q,
      },
    }
  }

  return {
    props: {
      ...props,
      item: page as Page,
      q,
    },
  }
}

const SearchResults = PageHOC<SearchResultsProps>(({
  item,
  q,
}) => {
  const components = useBlockComponents(item.blocks || [])

  return (
    <>
      <Head>
        <title>{`${item.seo?.title}: ${q}`}</title>
      </Head>
      <OneColumn footer={<FooterWithGroupVisibility />} header={<HeaderWithGroupVisibility />}>
        <h1
          style={{
            fontSize: '26px',
            fontWeight: 'normal',
            paddingBottom: '6px',
            paddingInline: '12px',
            textTransform: 'none',
            width: '100%',
          }}
        >
          {item.seo?.title ? `${item.seo.title} ` : null}
          &#8220;
          <strong>{q}</strong>
          &#8221;
        </h1>
        {components}
      </OneColumn>
      <BackToTop />
    </>
  )
})

export default SearchResults
